import { storyBestScore, storyBestScoreAfterUndos } from './leaderboardRules';
import type { Difficulty, GameMode, GameState, OpponentRecord, PlayerProfile, Side } from './types';

export const DIFFICULTY_ORDER: Difficulty[] = [
  'beginner', 'trader', 'cannonhorse', 'gambiteer', 'attacker', 'defender',
  'pawn', 'miser', 'blockader', 'scholar', 'veteran', 'master',
];

export interface OutcomeReward {
  recorded: boolean;
  points: number;
  basePoints: number;
  undoPenalty: number;
  result: 'win' | 'draw' | 'loss';
}

export function emptyOpponentRecords(): Record<Difficulty, OpponentRecord> {
  return Object.fromEntries(DIFFICULTY_ORDER.map((id) => [id, { wins: 0, draws: 0, losses: 0 }])) as Record<Difficulty, OpponentRecord>;
}

export function canChangeOpponent(mode: GameMode, state: GameState): boolean {
  return mode === 'practice' || state.moves.length === 0 || state.outcome !== 'ongoing';
}

export function canUndo(_mode: GameMode, _undosUsed: number): boolean {
  return true;
}

export function isResumableGame(state: GameState): boolean {
  return state.outcome === 'ongoing' && state.moves.length > 0;
}

export function nextDifficulty(current: Difficulty): Difficulty {
  return DIFFICULTY_ORDER[(DIFFICULTY_ORDER.indexOf(current) + 1) % DIFFICULTY_ORDER.length]!;
}

export function hasEncounteredOpponent(profile: PlayerProfile, difficulty: Difficulty): boolean {
  const record = profile.opponentRecords[difficulty];
  return record.wins + record.draws + record.losses > 0;
}

export function isOpponentRevealed(profile: PlayerProfile, difficulty: Difficulty): boolean {
  if (profile.masterWins > 0) return true;
  const index = DIFFICULTY_ORDER.indexOf(difficulty);
  if (index <= 0) return true;
  if (hasEncounteredOpponent(profile, difficulty)) return true;
  const previous = DIFFICULTY_ORDER[index - 1]!;
  return profile.opponentRecords[previous].wins > 0;
}

export function isOpponentAvailable(mode: GameMode, profile: PlayerProfile, difficulty: Difficulty): boolean {
  return mode === 'practice' || isOpponentRevealed(profile, difficulty);
}

export function highestRevealedDifficulty(profile: PlayerProfile): Difficulty {
  return [...DIFFICULTY_ORDER].reverse().find((id) => isOpponentRevealed(profile, id)) ?? 'beginner';
}

export function recordChallengeOutcome(
  source: PlayerProfile,
  state: GameState,
  difficulty: Difficulty,
  mode: GameMode,
  playerSide: Side = 'red',
  undosUsed = 0,
): { profile: PlayerProfile; reward: OutcomeReward | null } {
  if (state.outcome === 'ongoing' || source.recordedGameIds.includes(state.gameId)) {
    return { profile: source, reward: null };
  }

  const opponentSide: Side = playerSide === 'red' ? 'black' : 'red';
  const result = state.outcome === `${playerSide}_win` ? 'win' : state.outcome === `${opponentSide}_win` ? 'loss' : 'draw';
  if (mode !== 'challenge') return { profile: source, reward: { recorded: false, points: 0, basePoints: 0, undoPenalty: 0, result } };

  const basePoints = storyBestScore(difficulty, result);
  const points = storyBestScoreAfterUndos(difficulty, result, undosUsed);
  const undoPenalty = Math.max(0, basePoints - points);
  const profile: PlayerProfile = {
    ...source,
    opponentRecords: Object.fromEntries(
      DIFFICULTY_ORDER.map((id) => [id, { ...source.opponentRecords[id] }]),
    ) as PlayerProfile['opponentRecords'],
    recordedGameIds: [...source.recordedGameIds, state.gameId].slice(-100),
  };
  profile.score = Math.max(0, profile.score + points);
  if (result === 'win') {
    profile.wins += 1;
    profile.opponentRecords[difficulty].wins += 1;
    if (difficulty === 'master') profile.masterWins += 1;
  } else if (result === 'loss') {
    profile.losses += 1;
    profile.opponentRecords[difficulty].losses += 1;
  } else {
    profile.draws += 1;
    profile.opponentRecords[difficulty].draws += 1;
  }
  return { profile, reward: { recorded: true, points, basePoints, undoPenalty, result } };
}
